/**
 * SegmentDetailDrawer — right-side drawer that wraps SegmentDetail.
 *
 * Used by the "cards" view of the Segments tab. Reuses SegmentDetail as-is;
 * the drawer only owns the shell (scrim, slide-in, focus handling).
 *
 * Keep the drawer always mounted when a segment is selected, so transitions
 * only toggle `transform` / `opacity` (per motion rules).
 */

import { useEffect, useRef } from 'react'
import type { ComponentProps } from 'react'
import { X } from 'lucide-react'
import { SegmentDetail } from './SegmentDetail'

interface Props {
  open: boolean
  onClose: () => void
  /** SegmentDetail props forwarded verbatim. */
  detailProps: ComponentProps<typeof SegmentDetail>
}

export function SegmentDetailDrawer({ open, onClose, detailProps }: Props) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const lastFocusRef = useRef<HTMLElement | null>(null)

  // Lock background scroll while open; restore on close.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // Capture the previously focused element on open, restore on close.
  useEffect(() => {
    if (open) {
      lastFocusRef.current = (document.activeElement as HTMLElement) ?? null
      // Defer focus so the drawer's transform transition has begun.
      const id = requestAnimationFrame(() => closeBtnRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
    lastFocusRef.current?.focus?.()
    return undefined
  }, [open])

  // Escape to close.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  return (
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{
          backgroundColor: 'rgba(29, 26, 21, 0.34)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
      />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-labelledby="segment-detail-drawer-title"
        data-testid="segment-detail-drawer"
        data-state={open ? 'open' : 'closed'}
        className="fixed right-0 top-0 z-50 flex h-screen flex-col overflow-hidden bg-white shadow-[0_18px_40px_rgba(29,26,21,0.12),0_6px_14px_rgba(29,26,21,0.06)]"
        style={{
          width: 'min(920px, 94vw)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 260ms cubic-bezier(0.2, 0, 0, 1)',
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ backgroundColor: 'var(--color-warm-50)' }}
        >
          <span
            id="segment-detail-drawer-title"
            className="text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Segment detail
          </span>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close detail"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-white"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <SegmentDetail {...detailProps} />
        </div>
      </aside>
    </>
  )
}
